import { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Stack,
  CircularProgress,
  type SelectChangeEvent,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  type TestConfig,
  type WcagVersion,
  type WcagLevel,
  type WcagCriterion,
} from '@services/testConfigService';

const VERSIONS: WcagVersion[] = ['2.0', '2.1', '2.2'];
const LEVELS: WcagLevel[] = ['A', 'AA', 'AAA'];
// A sentinel for "inherit / not set at this level" so a context can leave a
// field to be inherited from the org (or platform defaults).
const INHERIT = '';

export interface TestConfigFormProps {
  /** The partial config stored at this level (org or context). */
  value: TestConfig;
  /**
   * REV-486: what a field left unset will actually resolve to — the org
   * defaults for a context, the platform defaults for an org. The API already
   * returns these alongside the stored config; the UI was discarding them, so
   * an unset field rendered blank and its "Inherit" option looked like it
   * selected nothing.
   */
  defaults?: TestConfig;
  /**
   * The WCAG catalog to offer in the excluded-criteria type-ahead. Passed in
   * rather than fetched here: this stays a presentational form, so a test can
   * render it without stubbing a service, and the catalog is fetched once by
   * the section rather than on every form render.
   */
  criteria?: WcagCriterion[];
  /** Persist the assembled partial config. */
  onSave: (config: TestConfig) => Promise<void>;
  saving?: boolean;
  /** Heading/help copy differ slightly between org-default and context-override. */
  level: 'organization' | 'context';
}

/**
 * Whether an inherited value is absent. Shared so `inheritedAs` and
 * `inheritHelper` cannot drift: before this existed they disagreed on `''`,
 * rendering a dangling "Inherits " with nothing after it.
 *
 * Deliberately not a truthiness check — `0` is a real value.
 */
const isUnset = (v?: string | number | null) => v === undefined || v === null || v === '';

/**
 * REV-461/462: edit a level's partial test configuration. Fields left as
 * "Inherit" are omitted from the saved partial so they fall through to the
 * lower-precedence layer (context -> org -> platform defaults).
 *
 * v1 covers the highest-value surface from the walkthrough — WCAG
 * version/level/excluded SC plus viewport + timeout. The remaining atest
 * runtime options (cookies, geolocation, headers, …) are a follow-up.
 */
export default function TestConfigForm({
  value,
  defaults,
  criteria = [],
  onSave,
  saving,
  level,
}: TestConfigFormProps) {
  const { t } = useTranslation();

  const [version, setVersion] = useState<string>(INHERIT);
  const [wcagLevel, setWcagLevel] = useState<string>(INHERIT);
  const [excluded, setExcluded] = useState<string[]>([]);
  const [vpWidth, setVpWidth] = useState<string>('');
  const [vpHeight, setVpHeight] = useState<string>('');
  const [timeout, setTimeoutMs] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  // Hydrate from the incoming value whenever it changes.
  useEffect(() => {
    setVersion(value.wcag?.version ?? INHERIT);
    setWcagLevel(value.wcag?.level ?? INHERIT);
    setExcluded(value.wcag?.excludedCriteria ?? []);
    setVpWidth(value.runtime?.viewport?.width ? String(value.runtime.viewport.width) : '');
    setVpHeight(value.runtime?.viewport?.height ? String(value.runtime.viewport.height) : '');
    setTimeoutMs(value.runtime?.timeout ? String(value.runtime.timeout) : '');
  }, [value]);

  const handleSubmit = async () => {
    setError(null);
    const excludedList = excluded;

    let viewport: { width: number; height: number } | undefined;
    if (vpWidth || vpHeight) {
      const w = Number(vpWidth);
      const h = Number(vpHeight);
      if (!Number.isInteger(w) || !Number.isInteger(h) || w < 320 || h < 240) {
        setError(
          t(
            'testConfig.errors.viewport',
            'Viewport width (≥320) and height (≥240) must be whole numbers.'
          )
        );
        return;
      }
      viewport = { width: w, height: h };
    }
    let timeoutMs: number | undefined;
    if (timeout) {
      const ms = Number(timeout);
      if (!Number.isInteger(ms) || ms < 5000 || ms > 120000) {
        setError(t('testConfig.errors.timeout', 'Timeout must be between 5000 and 120000 ms.'));
        return;
      }
      timeoutMs = ms;
    }

    // Start from the incoming config so fields this form doesn't manage
    // (authentication, cookies, headers, rules, …) are PRESERVED rather than
    // wiped — the form only owns wcag version/level/excludedCriteria + viewport
    // + timeout. A managed field left blank is deleted so it inherits again.
    const config: TestConfig = JSON.parse(JSON.stringify(value || {}));

    const wcag = { ...(config.wcag || {}) };
    if (version) wcag.version = version as WcagVersion;
    else delete wcag.version;
    if (wcagLevel) wcag.level = wcagLevel as WcagLevel;
    else delete wcag.level;
    if (excludedList.length > 0) wcag.excludedCriteria = excludedList;
    else delete wcag.excludedCriteria;
    if (Object.keys(wcag).length > 0) config.wcag = wcag;
    else delete config.wcag;

    const runtime = { ...(config.runtime || {}) };
    if (viewport) runtime.viewport = viewport;
    else delete runtime.viewport;
    if (timeoutMs !== undefined) runtime.timeout = timeoutMs;
    else delete runtime.timeout;
    if (Object.keys(runtime).length > 0) config.runtime = runtime;
    else delete config.runtime;

    await onSave(config);
  };

  /**
   * Label for the inherit option. Names the value rather than just saying
   * "Inherit" — the complaint was that an unset field gave the user nothing to
   * go on. Falls back to the bare word when the caller passed no defaults, so
   * the control never renders worse than it did before.
   */
  // Catalog ids plus anything already stored, so an exclusion set under a
  // different WCAG version still appears (and survives a save).
  const criteriaIds = Array.from(new Set([...criteria.map((c) => c.sc), ...excluded]));

  /**
   * REV-574: the inherited value as a description, shown only while the field
   * is empty (i.e. actually inheriting).
   *
   * The placeholder alone would satisfy the letter of the ticket, but
   * placeholder text disappears on input and is inconsistently exposed to
   * assistive tech — MUI wires helperText to the input via aria-describedby,
   * so this is the form that is actually announced. In an accessibility
   * product, "the sighted user can see what they'll inherit" is not enough.
   *
   * Both carry the same string on purpose. Per the accname spec the description
   * comes from aria-describedby when it is present and placeholder is only a
   * fallback, so conformant AT reads it once; dropping either would cost a real
   * affordance (placeholder is the sighted cue, this is the announced one).
   */
  /**
   * REV-690: the helper now names WHERE the value comes from, not just that it
   * is inherited.
   *
   * REV-574 put "Inherit (1920)" in the placeholder and "Inherits 1920" in the
   * helper. Read together on screen that is "1920 inherit", which is how the
   * walkthrough reported it — the same number twice, with the word "Inherit"
   * dominating the field so it looked unset rather than settled.
   *
   * The value stays in the helper deliberately. It is the string MUI wires to
   * the input via aria-describedby, so it is the one assistive tech actually
   * announces; moving the number out to the placeholder alone would make the
   * effective value sighted-only, which is not acceptable in this product.
   */
  const inheritSource =
    level === 'context'
      ? t('testConfig.fromOrganization', 'the organization')
      : t('testConfig.fromSystemDefaults', 'the system defaults');

  const inheritHelper = (current: string, v?: string | number | null) =>
    current === '' && !isUnset(v)
      ? t('testConfig.inheritsValueFrom', 'Inherits {{value}} from {{source}}', {
          value: String(v),
          source: inheritSource,
        })
      : undefined;

  const inheritLabel = t('testConfig.inherit', 'Inherit');

  /**
   * REV-690: the placeholder is the bare value, not "Inherit (1920)".
   *
   * The walkthrough's ask was that accepting the defaults should leave the
   * values visible in the fields, "because that's what their settings are".
   * A greyed 1920 reads as the value; "Inherit (1920)" reads as an absence.
   *
   * The field itself stays EMPTY, and that is not a detail. An empty field is
   * what "inherit" means here: typing 1920 into it would turn a value that
   * follows the organization into a fixed override, so a later change to the
   * organization default would silently stop reaching this tracked place.
   * Showing the value and storing the value are different things, and only the
   * first was asked for.
   */
  const inheritedAs = (v?: string | number | null) => (isUnset(v) ? inheritLabel : String(v));

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {level === 'organization'
          ? t(
              'testConfig.orgHelp',
              'Defaults for every tracked place in this organization. Tracked places and individual scans can override any field.'
            )
          : t(
              'testConfig.contextHelp',
              'Overrides for this tracked place. Fields left as "Inherit" use the organization defaults.'
            )}
      </Typography>

      {error && (
        <Typography role="alert" color="error" variant="body2" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Stack spacing={2} sx={{ maxWidth: 520 }}>
        <FormControl size="small" fullWidth>
          <InputLabel id="tc-version">{t('testConfig.wcagVersion', 'WCAG version')}</InputLabel>
          <Select
            labelId="tc-version"
            value={version}
            // REV-486: without displayEmpty, MUI renders NOTHING for value ===
            // '' — which is what INHERIT is. That is why the field looked blank
            // and its "Inherit" option appeared to select nothing: the option
            // was selected, MUI just refused to display it.
            displayEmpty
            label={t('testConfig.wcagVersion', 'WCAG version')}
            onChange={(e: SelectChangeEvent) => setVersion(e.target.value)}
          >
            <MenuItem value={INHERIT}>
              <em>{inheritedAs(defaults?.wcag?.version)}</em>
            </MenuItem>
            {VERSIONS.map((v) => (
              <MenuItem key={v} value={v}>
                {t('common.wcagVersion', 'WCAG {{version}}', { version: v })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth>
          <InputLabel id="tc-level">{t('testConfig.wcagLevel', 'WCAG level')}</InputLabel>
          <Select
            labelId="tc-level"
            value={wcagLevel}
            displayEmpty
            label={t('testConfig.wcagLevel', 'WCAG level')}
            onChange={(e: SelectChangeEvent) => setWcagLevel(e.target.value)}
          >
            <MenuItem value={INHERIT}>
              <em>{inheritedAs(defaults?.wcag?.level)}</em>
            </MenuItem>
            {LEVELS.map((l) => (
              <MenuItem key={l} value={l}>
                {l}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* REV-486: a type-ahead over the real catalog. The old comma-separated
            TextField required knowing SC ids by heart and accepted a typo
            silently — excluding nothing while looking like it had. freeSolo is
            deliberately OFF so only real criteria can be chosen. */}
        {/* REV-486: a type-ahead over the real catalog. The old comma-separated
            TextField required knowing SC ids by heart and accepted a typo
            silently — excluding nothing while looking like it had.

            Works in ID strings, not catalog objects, on purpose: a stored id
            the catalog does not contain (an older version, or a catalog that
            failed to load) must still round-trip. Filtering the stored list
            against the catalog would silently delete a user's exclusions the
            moment they opened the form. */}
        <Autocomplete
          multiple
          freeSolo
          size="small"
          id="tc-excluded"
          options={criteriaIds}
          value={excluded}
          getOptionLabel={(id) => {
            const hit = criteria.find((c) => c.sc === id);
            return hit ? `${hit.sc} ${hit.title} (${hit.level})` : String(id);
          }}
          onChange={(_e, sel) =>
            setExcluded(sel.map((s) => String(s).split(' ')[0] ?? '').filter(Boolean))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('testConfig.excludedCriteria', 'Excluded success criteria')}
              helperText={t(
                'testConfig.excludedHelpTypeahead',
                'Search by number or name. Criteria outside the selected WCAG version are kept if already set.'
              )}
            />
          )}
        />

        <Stack direction="row" spacing={2}>
          <TextField
            size="small"
            label={t('testConfig.viewportWidth', 'Viewport width')}
            value={vpWidth}
            onChange={(e) => setVpWidth(e.target.value)}
            // REV-574: name the value, like the WCAG fields above. Bare
            // "Inherit" told the user a value was coming from somewhere
            // without saying what it was.
            placeholder={inheritedAs(defaults?.runtime?.viewport?.width)}
            helperText={inheritHelper(vpWidth, defaults?.runtime?.viewport?.width)}
            inputProps={{ inputMode: 'numeric' }}
          />
          <TextField
            size="small"
            label={t('testConfig.viewportHeight', 'Viewport height')}
            value={vpHeight}
            onChange={(e) => setVpHeight(e.target.value)}
            placeholder={inheritedAs(defaults?.runtime?.viewport?.height)}
            helperText={inheritHelper(vpHeight, defaults?.runtime?.viewport?.height)}
            inputProps={{ inputMode: 'numeric' }}
          />
        </Stack>

        <TextField
          size="small"
          label={t('testConfig.timeout', 'Timeout (ms)')}
          value={timeout}
          onChange={(e) => setTimeoutMs(e.target.value)}
          placeholder={inheritedAs(defaults?.runtime?.timeout)}
          helperText={inheritHelper(timeout, defaults?.runtime?.timeout)}
          inputProps={{ inputMode: 'numeric' }}
          sx={{ maxWidth: 240 }}
        />

        <Box>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : t('testConfig.save', 'Save configuration')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
